import { useEffect, useMemo } from 'react'
import type { RefObject } from 'react'

import type { MapCanvasHandle } from './MapCanvas'
import { fixFromProps, forecastCollection, forecastRun } from './vesselForecast'

/**
 * Прогнозный ход выбранного судна на карте.
 *
 * Считается ЦЕЛИКОМ в браузере: курс, скорость и время фикса приезжают вместе с
 * позицией, и спрашивать за ними сервер не о чем. Возвращает наружу причину
 * отказа и оговорку — карточка обязана сказать, почему линии нет или почему она
 * мягкая: молчащий тумблер неотличим от сломанного.
 */
export function useVesselForecast(
  mapRef: RefObject<MapCanvasHandle | null>,
  enabled: boolean,
  props: Record<string, unknown> | null,
  anchor: { lng: number; lat: number } | null,
): { reasonKey: string | null; noticeKey: string | null } {
  // Решение «рисовать ли и с каким доверием» от кадра не зависит — только от
  // самого фикса. Его и запоминаем; геометрия считается ниже, в эффекте.
  const fix = useMemo(
    () => (props && anchor ? fixFromProps(props, anchor) : null),
    [props, anchor],
  )
  const verdict = useMemo(() => (fix ? forecastRun(fix) : null), [fix])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (!enabled || !fix || !verdict?.drawn) {
      map.removeVesselForecast('vessel')
      return
    }
    // Долгота центра берётся В МОМЕНТ ОТРИСОВКИ, а не на рендере: она приводит
    // линию в тот оборот мира, в котором СЕЙЧАС стоит карта. Прочитанная
    // раньше, за 180-м меридианом она увела бы линию на целый мир от кадра.
    const centerLng = map.getMap()?.getCenter().lng
    const placed = forecastRun(fix, { centerLng })
    if (!placed.drawn) return
    map.setVesselForecast('vessel', forecastCollection(placed))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, fix, verdict])

  // Уход со страницы — слой за собой убираем.
  useEffect(
    () => () => {
      mapRef.current?.removeVesselForecast('vessel')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  if (!enabled || !verdict) return { reasonKey: null, noticeKey: null }
  if (!verdict.drawn) return { reasonKey: verdict.reasonKey, noticeKey: null }
  return { reasonKey: null, noticeKey: verdict.noticeKey }
}
